import {
    PropsWithChildren,
    ReactNode,
    useCallback,
    useEffect,
    useState,
} from 'react';

import { ModalDumb } from './Modal.dumb';
import { ModalBackground, ModalContainer } from './Modal.styled';

/* ---------------------------------- Types --------------------------------- */

/**
 * ModalRenderPropsI
 *
 * Props made available when rendering the children of the modal
 */
interface PortalRenderPropsI {
    /** Indicates whether the Modal should be visible  */
    showModal: boolean;

    /** Used to open the modal in other components */
    openModal: () => void;

    /** Used to close the modal in other components */
    closeModal: () => void;

    /** Modal component to wrap around modal content */
    Modal: React.FC<PropsWithChildren>;
}

/**
 * PortalI
 *
 * Props for the Portal component
 */
interface PortalI {
    /** children prop is a render function instead of a React component */
    children: (props: PortalRenderPropsI) => ReactNode;

    /** Flag for if the user can dismiss the modal */
    canDismiss?: boolean;

    /** Flag for if the modal should be visible on first render */
    initialShowModal?: boolean;

    /** Callback for when the modal visibility changes */
    onShowChange?: (show: boolean) => void;
}

/**
 * Portal
 *
 * The Portal component is a renderless component that manages the functionality of the Modal without any opinions on
 * what should be displayed. It provides render props to be consumed in situ with the UI being displayed.
 *
 * @param param0
 * @returns
 */
export const Portal = ({
    canDismiss = true,
    children,
    initialShowModal = false,
    onShowChange,
}: PortalI) => {
    const [showModal, setShowModal] = useState(initialShowModal);

    useEffect(() => {
        setShowModal(initialShowModal);
    }, [initialShowModal]);

    // Define modal state callbacks
    const openModal = useCallback(() => {
        setShowModal(true);
        onShowChange?.(true);
    }, [onShowChange]);

    const closeModal = useCallback(() => {
        setShowModal(false);
        onShowChange?.(false);
    }, [onShowChange]);

    // Basic Modal component to be returned in the render props
    const Modal = useCallback(
        ({ children: c }: PropsWithChildren) =>
            showModal ? (
                <ModalDumb>
                    <ModalBackground
                        onClick={(e) => {
                            if (canDismiss && e.target === e.currentTarget)
                                closeModal();
                        }}
                    >
                        <ModalContainer>{c}</ModalContainer>
                    </ModalBackground>
                </ModalDumb>
            ) : null,
        [showModal, canDismiss, closeModal],
    );

    // Instead of being a React component, the children prop is a function
    return children({
        closeModal,
        showModal,
        Modal,
        openModal,
    });
};
